<template>
  <div class="trace-logs">
    <div v-if="loading" class="logs-note">Loading logs…</div>
    <div v-else-if="error" class="logs-note err">
      Couldn't load this trace's logs.
      <button class="link-btn" @click="load">Retry</button>
    </div>

    <div v-else-if="!logs.length" class="logs-empty">
      <GoblinIcon variant="empty" :size="48" />
      <p class="logs-empty-title">No logs matched this trace</p>
      <p class="logs-empty-sub">
        Nothing was logged in this trace's time window ({{ windowLabel }}).
      </p>
    </div>

    <template v-else>
      <p class="logs-window">
        Logs from this trace's containers during its time window ({{ windowLabel }}).
      </p>
      <div class="logs-rows">
        <LogRow
          v-for="row in logs"
          :key="row.id"
          :row="row"
          :expanded="expandedId === row.id"
          @toggle="toggleExpand(row.id)"
        />
      </div>
      <p v-if="hasMore" class="logs-more">
        Showing the first {{ logs.length }} lines — there are more in this window. Open the Logs
        view for the full picture.
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { api, ApiError, type LogRow as LogRowT } from '../../api.js';
import { formatClockTime } from '../../lib/format-metric.js';
import LogRow from '../LogRow.vue';
import GoblinIcon from '../GoblinIcon.vue';

const props = defineProps<{ traceId: string }>();
const emit = defineEmits<{ unauthorized: [] }>();

const logs = ref<LogRowT[]>([]);
const logWindow = ref<{ startMs: number; endMs: number } | null>(null);
const hasMore = ref(false);
const loading = ref(false);
const error = ref(false);
const expandedId = ref<number | null>(null);
let loadSeq = 0;

const windowLabel = computed(() => {
  if (!logWindow.value) return '';
  const { startMs, endMs } = logWindow.value;
  return `${formatClockTime(startMs, { seconds: true })} – ${formatClockTime(endMs, { seconds: true })}`;
});

async function load() {
  const seq = ++loadSeq;
  loading.value = true;
  error.value = false;
  expandedId.value = null;
  try {
    const res = await api.traces.logs(props.traceId);
    if (seq !== loadSeq) return;
    logs.value = res.logs;
    logWindow.value = res.window;
    hasMore.value = res.hasMore;
  } catch (e) {
    if (e instanceof ApiError && e.unauthorized) {
      emit('unauthorized');
      return;
    }
    if (seq !== loadSeq) return;
    logs.value = [];
    error.value = true;
  } finally {
    if (seq === loadSeq) loading.value = false;
  }
}

function toggleExpand(id: number) {
  expandedId.value = expandedId.value === id ? null : id;
}

watch(() => props.traceId, load, { immediate: true });
</script>

<style scoped>
.trace-logs {
  min-width: 0;
}
.logs-note {
  padding: var(--lg-space-3);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-sm);
}
.logs-note.err {
  color: var(--lg-danger);
}
.link-btn {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  text-decoration: underline;
  font: inherit;
}
.logs-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--lg-space-2);
  padding: var(--lg-space-6) var(--lg-space-3);
  text-align: center;
  color: var(--lg-text-faint);
}
.logs-empty-title {
  margin: 0;
  color: var(--lg-text-dim);
  font-size: var(--lg-fs-md);
}
.logs-empty-sub {
  margin: 0;
  font-size: var(--lg-fs-sm);
}
.logs-window {
  margin: 0 0 var(--lg-space-2);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-xs);
}
.logs-rows {
  border: 1px solid var(--lg-border-faint);
  border-radius: var(--lg-radius-md);
  overflow: hidden;
  background: var(--lg-bg);
}
.logs-more {
  margin: var(--lg-space-2) 0 0;
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-xs);
}
</style>
